import { ReactNode, useState } from 'react';
import { useTranslation } from '@sib/i18n';
import { Button, cn, Separator, UserAvatar } from '@sib/ui';
import { useClampOverflow } from '@sib/ui/hooks';
import { useDateFormat } from '@sib/utils';
import { format } from 'date-fns';

interface EntityCommentCardProps {
  authorName: string;
  authorAvatarUrl?: string | null;
  isAuthorDeactivated?: boolean;
  createdAt: string;
  comment: string;
  badge?: ReactNode;
}

export const EntityCommentCard = ({
  authorName,
  authorAvatarUrl,
  isAuthorDeactivated,
  createdAt,
  comment,
  badge,
}: EntityCommentCardProps) => {
  const { t } = useTranslation();
  const dateFormat = useDateFormat();
  const [isExpanded, setIsExpanded] = useState(false);
  const { ref: textRef, clamped } = useClampOverflow(!isExpanded);

  const showToggle = clamped || isExpanded;

  return (
    <div className="border-border bg-surface-subtle rounded-md border">
      <div className="flex items-center justify-between gap-3 px-4 py-3">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <UserAvatar
            variant="small"
            className="size-8"
            avatarImageProps={{ avatarUrl: authorAvatarUrl }}
            avatarFallbackProps={{
              name: authorName,
              className: 'text-xs font-medium',
            }}
          />
          <span className="text-primary truncate text-base font-semibold">
            {authorName}
            {isAuthorDeactivated && (
              <span className="text-muted-foreground ml-1 text-sm font-normal lowercase">
                ({t('deactivated')})
              </span>
            )}
          </span>
          {badge}
        </div>
        <span className="text-muted-foreground shrink-0 text-base">
          {format(new Date(createdAt), dateFormat.defaultDateTimeFormat)}
        </span>
      </div>
      <Separator className="mx-auto w-[calc(100%-2rem)]" />
      <div className="px-4 py-3">
        <p
          ref={textRef}
          className={cn(
            'text-muted-foreground text-sm leading-5 wrap-break-word whitespace-pre-wrap',
            !isExpanded && 'line-clamp-2'
          )}
        >
          {comment}
        </p>
        {showToggle && (
          <Button
            type="button"
            variant="link"
            className="text-primary h-auto p-0 text-sm font-semibold underline"
            onClick={() => setIsExpanded(prev => !prev)}
          >
            {isExpanded ? t('clamp.showLess') : t('clamp.showMore')}
          </Button>
        )}
      </div>
    </div>
  );
};
